// Auth API Service - Handles authentication and user profile
import api from './api';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  bio: string | null;
  photoUrl: string | null;
  websiteUrl: string | null;
  twitterUrl: string | null;
  linkedinUrl: string | null;
  githubUrl: string | null;
  instagramUrl: string | null;
  youtubeUrl: string | null;
  facebookUrl: string | null;
  tiktokUrl: string | null;
  phoneNumber: string | null;
  jobTitle: string | null;
  company: string | null;
  isAdmin: boolean;
  hasPassword: boolean;
  hasGoogleAuth: boolean;
  createdAt: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

interface AuthResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      displayName: string | null;
      bio: string | null;
      photoUrl: string | null;
      websiteUrl: string | null;
      twitterUrl: string | null;
      linkedinUrl: string | null;
      githubUrl: string | null;
      instagramUrl: string | null;
      youtubeUrl: string | null;
      facebookUrl: string | null;
      tiktokUrl: string | null;
      phoneNumber: string | null;
      jobTitle: string | null;
      company: string | null;
      isAdmin: boolean;
      hasPassword: boolean;
      hasGoogleAuth: boolean;
      createdAt: string;
    };
    token: string;
    refreshToken?: string; // Optional for backward compatibility
  };
}

class AuthService {
  private currentUser: AuthUser | null = null;

  async initialize(): Promise<AuthUser | null> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        this.currentUser = null;
        return null;
      }

      const response = await api.get<AuthResponse>('/auth/me');
      if (response.data.success) {
        const user = response.data.data.user;
        this.currentUser = {
          ...user,
        };
        localStorage.setItem('current_user', JSON.stringify(this.currentUser));
        return this.currentUser;
      }
      return null;
    } catch (error: any) {
      // Only log if it's not a 401/403 (which just means not authenticated)
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        console.error('Failed to initialize auth:', error);
      }
      this.clearSession();
      return null;
    }
  }

  getCurrentUser(): AuthUser | null {
    if (!this.currentUser) {
      const stored = localStorage.getItem('current_user');
      if (stored) {
        try {
          this.currentUser = JSON.parse(stored);
        } catch {
          this.currentUser = null;
        }
      }
    }
    return this.currentUser;
  }

  async register(input: RegisterInput): Promise<AuthUser> {
    try {
      const response = await api.post<AuthResponse>('/auth/register', input);

      if (response.data.success) {
        const user = response.data.data.user;
        const token = response.data.data.token;

        this.currentUser = {
          ...user,
          hasPassword: true,
          hasGoogleAuth: false,
        };

        localStorage.setItem('auth_token', token);
        localStorage.setItem('current_user', JSON.stringify(this.currentUser));

        return this.currentUser;
      }

      throw new Error('Registration failed');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  }

  async login(email: string, password: string, rememberMe: boolean = false): Promise<AuthUser> {
    try {
      const response = await api.post<AuthResponse>('/auth/login', { email, password, rememberMe });

      if (response.data.success) {
        const user = response.data.data.user;
        const token = response.data.data.token;

        this.currentUser = {
          ...user,
          hasPassword: true,
          hasGoogleAuth: false,
        };

        localStorage.setItem('auth_token', token);
        localStorage.setItem('current_user', JSON.stringify(this.currentUser));

        return this.currentUser;
      }

      throw new Error('Login failed');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearSession();
    }
  }

  clearSession(): void {
    this.currentUser = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
  }

  async updateProfile(updates: { firstName?: string; lastName?: string; email?: string; displayName?: string; bio?: string; photoUrl?: string; websiteUrl?: string; twitterUrl?: string; linkedinUrl?: string; githubUrl?: string; instagramUrl?: string; youtubeUrl?: string; facebookUrl?: string; tiktokUrl?: string; phoneNumber?: string; jobTitle?: string; company?: string }): Promise<AuthUser> {
    try {
      const response = await api.put<AuthResponse>('/auth/profile', updates);

      if (response.data.success) {
        const user = response.data.data.user;
        this.currentUser = {
          ...user,
        };
        localStorage.setItem('current_user', JSON.stringify(this.currentUser));
        return this.currentUser;
      }

      throw new Error('Profile update failed');
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Profile update failed');
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to change password');
    }
  }

  async setPassword(password: string): Promise<void> {
    try {
      await api.post('/auth/set-password', { password });
      // Reload user to get updated hasPassword flag
      await this.initialize();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to set password');
    }
  }

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<void> {
    try {
      await api.delete('/auth/account');
      this.clearSession();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete account');
    }
  }

  // ========== Private Helper Methods ==========

  private async refreshUser(): Promise<void> {
    try {
      const response = await api.get<AuthResponse>('/auth/me');
      if (response.data.success) {
        const user = response.data.data.user;
        this.currentUser = {
          ...user,
        };
        localStorage.setItem('current_user', JSON.stringify(this.currentUser));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }
}

export const authService = new AuthService();
export default authService;
